import { encode, decode } from "@msgpack/msgpack";
import { SocketMessageInterface } from "../interfaces/Monitoring.interface";
import {
  directionStringToEnum,
  doorZoneStringToEnum,
  escControlStringToEnum,
} from "../flatbuffers/types/enumDisplayUtils";

// LN2R binary replay format (v2):
//   [4 bytes] Magic: "LN2R" (0x4C 0x4E 0x32 0x52)
//   [4 bytes] Version: uint32 LE (currently 2)
//   [4 bytes] Message count: uint32 LE
//   Per message:
//     [8 bytes] Timestamp: float64 LE (epoch milliseconds)
//     [4 bytes] Length: uint32 LE
//     [N bytes] MessagePack-encoded SocketMessageInterface

export interface ReplayRecord {
  timestamp: number; // epoch milliseconds
  message: SocketMessageInterface;
}

const MAGIC = new Uint8Array([0x4c, 0x4e, 0x32, 0x52]); // "LN2R"
const VERSION = 2;
const HEADER_SIZE = 12; // 4 magic + 4 version + 4 count

export function encodeReplayFile(records: ReplayRecord[]): Blob {
  const encodedMessages = records.map((r) => encode(r.message));

  let totalSize = HEADER_SIZE;
  encodedMessages.forEach((buf) => {
    totalSize += 8 + 4 + buf.byteLength; // 8 timestamp + 4 length + N data
  });

  const buffer = new ArrayBuffer(totalSize);
  const view = new DataView(buffer);
  const bytes = new Uint8Array(buffer);

  // Write header
  bytes.set(MAGIC, 0);
  view.setUint32(4, VERSION, true);
  view.setUint32(8, records.length, true);

  // Write messages
  let offset = HEADER_SIZE;
  records.forEach((record, i) => {
    view.setFloat64(offset, record.timestamp, true);
    offset += 8;
    const buf = encodedMessages[i];
    view.setUint32(offset, buf.byteLength, true);
    offset += 4;
    bytes.set(
      new Uint8Array(buf.buffer, buf.byteOffset, buf.byteLength),
      offset
    );
    offset += buf.byteLength;
  });

  return new Blob([buffer], { type: "application/octet-stream" });
}

export function decodeReplayFile(buffer: ArrayBuffer): ReplayRecord[] {
  if (buffer.byteLength < HEADER_SIZE) {
    throw new Error("File too small to be a valid LN2R replay file");
  }

  const view = new DataView(buffer);
  const bytes = new Uint8Array(buffer);

  // Validate magic
  for (let i = 0; i < 4; i += 1) {
    if (bytes[i] !== MAGIC[i]) {
      throw new Error("Invalid file format: missing LN2R magic header");
    }
  }

  // Validate version
  const version = view.getUint32(4, true);
  if (version !== VERSION) {
    throw new Error(
      `Unsupported LN2R version: ${version} (expected ${VERSION})`
    );
  }

  const count = view.getUint32(8, true);
  const records: ReplayRecord[] = [];
  let offset = HEADER_SIZE;

  for (let i = 0; i < count; i += 1) {
    if (offset + 12 > buffer.byteLength) {
      throw new Error(`Unexpected end of file at message ${i}`);
    }
    const timestamp = view.getFloat64(offset, true);
    offset += 8;
    const len = view.getUint32(offset, true);
    offset += 4;
    if (offset + len > buffer.byteLength) {
      throw new Error(`Unexpected end of file at message ${i} data`);
    }
    const msgBytes = bytes.slice(offset, offset + len);
    const raw = decode(msgBytes) as Record<string, unknown>;
    // Normalize old string-format replay files to enum values
    if (typeof raw.direction === "string") {
      raw.direction = directionStringToEnum(raw.direction);
    }
    if (typeof raw.door_zone === "string") {
      raw.door_zone = doorZoneStringToEnum(raw.door_zone);
    }
    if (typeof raw.escalator_control === "string") {
      raw.escalator_control = escControlStringToEnum(raw.escalator_control);
    }
    records.push({
      timestamp,
      message: raw as unknown as SocketMessageInterface,
    });
    offset += len;
  }

  return records;
}
